import { BasePageProps, UnknownObject, UseQueryReturnProps } from 'types';

import { joinStrings } from './helpers';
import { filterEmptyFields } from './object';

export const addParamsToUrl = (
  url: string,
  params?: Record<string, any>
): string => {
  if (!params) {
    return url;
  }
  const urlParams = new URLSearchParams();
  Object.keys(filterEmptyFields(params)).forEach((key) =>
    urlParams.append(key, params[key])
  );
  return `${url}?${urlParams.toString()}`;
};

export const mapQuery = ({
  error,
  isFetching,
  ...props
}: UseQueryReturnProps): BasePageProps => {
  return {
    ...props,
    error: error?.message,
    isLoading: isFetching,
  };
};

export type Refetches<T> = Record<keyof T, () => void>;

export interface MapQueriesReturnProps<T> extends BasePageProps {
  refetchAll: () => void;
  refetches: Refetches<T>;
}

interface MapQueriesOptions {
  /** Keep cached content visible while queries refetch in the background. */
  initialLoadOnly?: boolean;
}

export const mapQueries = <T extends UnknownObject>(
  queries: Record<keyof T, UseQueryReturnProps<T[keyof T]>>,
  { initialLoadOnly = false }: MapQueriesOptions = {}
): T & MapQueriesReturnProps<T> => {
  const dataProps = {} as T;
  const refetches = {} as Refetches<T>;

  const queriesState = Object.entries(queries).reduce<{
    errorStates: boolean[];
    loadingStates: boolean[];
    errors: (string | undefined)[];
    refetchFunctions: (() => void)[];
  }>(
    (result, [name, query]) => {
      const { data, error, refetch, isError, isLoading, isFetching } = query;

      if (data) {
        dataProps[name as keyof T] = data;
      }

      refetches[name as keyof T] = refetch;

      result.loadingStates.push(initialLoadOnly ? isLoading : isFetching);
      result.errorStates.push(isError);

      if (isError) {
        result.errors.push(error?.message);
        result.refetchFunctions.push(refetch);
      }

      return result;
    },
    {
      errors: [],
      errorStates: [],
      loadingStates: [],
      refetchFunctions: [],
    }
  );

  return {
    refetches,
    isError: queriesState.errorStates.some(Boolean),
    isLoading: queriesState.loadingStates.some(Boolean),
    refetchAll: () =>
      Object.values(refetches).forEach((fn) => {
        try {
          fn();
        } catch (error) {
          console.error(error);
        }
      }),
    refetch: () =>
      queriesState.refetchFunctions.forEach((refetch) => {
        try {
          refetch();
        } catch (error) {
          console.error(error);
        }
      }),
    error:
      queriesState.errors.filter(Boolean).length > 0
        ? joinStrings(
            Array.from(new Set(queriesState.errors as string[])),
            '\n'
          )
        : undefined,
    ...dataProps,
  };
};
